import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toast';
import {
  LuArrowRight,
  LuCalendar,
  LuCrown,
  LuExternalLink,
  LuLayoutGrid,
  LuList,
  LuPlus,
  LuTrash2,
  LuUsers,
  LuX,
} from 'react-icons/lu';
import { useWorkspaceList, addWorkspace, deleteWorkspace, useUpdateWorkspace } from '../../hooks/useWorkspace';
import { useDebounce } from '../../hooks/useDebounce';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useProject } from '../../context/ProjectContext';
import WorkspaceProjectSelectModal from './WorkspaceProjectSelectModal';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import {
  ActionMenu,
  Breadcrumbs,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  PageScaffold,
  Pagination,
  SearchInput,
} from '../ui';
import '../../styles/all-workspaces.css';
import { formatDate } from '../../utils/dateTimeFormat';

const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];
const NAME_MAX_LENGTH = 100;
const VIEW_STORAGE_KEY = 'rt-workspaces-view';

const getPageFromSearchParams = (searchParams) => {
  const page = Number.parseInt(searchParams.get('page'), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const getPageSizeFromSearchParams = (searchParams) => {
  const size = Number.parseInt(searchParams.get('page_size'), 10);
  return PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
};

const resolveWorkspaceListResponse = (workspacesData = {}, currentPage = 1) => {
  const responseData = workspacesData?.data;
  const workspaces = Array.isArray(responseData)
    ? responseData
    : Array.isArray(responseData?.workspaces)
      ? responseData.workspaces
      : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(workspacesData?.workspaces)
          ? workspacesData.workspaces
          : [];

  const pageSize =
    Number(
      workspacesData?.page_size
        ?? responseData?.page_size
        ?? workspacesData?.pagination?.page_size
        ?? responseData?.pagination?.page_size
        ?? DEFAULT_PAGE_SIZE
    ) || DEFAULT_PAGE_SIZE;

  const totalCount = Number(
    workspacesData?.total_records
      ?? workspacesData?.total_count
      ?? responseData?.total_count
      ?? workspacesData?.total
      ?? responseData?.total
      ?? workspacesData?.pagination?.total_count
      ?? responseData?.pagination?.total_count
      ?? 0
  );

  const totalPagesFromApi = Number(
    workspacesData?.total_pages
      ?? responseData?.total_pages
      ?? workspacesData?.pagination?.total_pages
      ?? responseData?.pagination?.total_pages
      ?? 0
  );

  let totalPages =
    totalPagesFromApi > 0
      ? totalPagesFromApi
      : totalCount > 0
        ? Math.ceil(totalCount / pageSize)
        : 0;

  // Fallback when API paginates results but omits total metadata
  if (totalPages <= 0 && workspaces.length > 0) {
    totalPages = workspaces.length < pageSize ? currentPage : currentPage + 1;
  }

  return {
    workspaces,
    totalPages,
    pageSize,
    totalCount: totalCount || workspaces.length,
    lastWorkspace: workspacesData?.last_workspace_data || null,
    lastProject: workspacesData?.last_project_data || null,
  };
};

const getCurrentUserId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.userId ?? stored?.user_id ?? stored?.id ?? null;
  } catch {
    return null;
  }
};

const workspaceHue = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return hash;
};

const workspaceInitials = (name = '') =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'W';

const formatCreated = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Created today';
  if (days === 1) return 'Created yesterday';
  if (days < 30) return `Created ${days} days ago`;
  return `Created ${formatDate(date)}`;
};

const WorkspaceTile = ({ name, size = 'md' }) => (
  <span
    className={`rt-ws-tile rt-ws-tile--${size}`}
    style={{ '--ws-hue': workspaceHue(name) }}
    aria-hidden="true"
  >
    {workspaceInitials(name)}
  </span>
);

const RoleChip = ({ isOwner }) => (
  <span className={`rt-ws-role${isOwner ? ' rt-ws-role--owner' : ''}`}>
    {isOwner ? <LuCrown size={12} aria-hidden="true" /> : <LuUsers size={12} aria-hidden="true" />}
    {isOwner ? 'Owner' : 'Member'}
  </span>
);

const AllWorkspaces = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { selectWorkspace, currentWorkspace } = useWorkspace();
  const { selectProject } = useProject();
  const currentPage = getPageFromSearchParams(searchParams);
  const pageSize = getPageSizeFromSearchParams(searchParams);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data: workspacesData = {}, error, refetch, isLoading } = useWorkspaceList({
    page: currentPage,
    pageSize,
    search: debouncedSearchTerm,
  });
  const { workspaces, totalPages, totalCount, lastWorkspace, lastProject } =
    resolveWorkspaceListResponse(workspacesData, currentPage);

  const { mutate: updateWorkspaceFn } = useUpdateWorkspace();
  const currentUserId = useMemo(getCurrentUserId, []);

  const [view, setView] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || 'grid');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);

  const activeWorkspaceId = currentWorkspace?.id ?? lastWorkspace?.id ?? null;
  const isOwner = useCallback(
    (workspace) => currentUserId != null && String(workspace?.user_id) === String(currentUserId),
    [currentUserId]
  );
  const isActive = useCallback(
    (workspace) => activeWorkspaceId != null && String(workspace?.id) === String(activeWorkspaceId),
    [activeWorkspaceId]
  );

  const changeView = (next) => {
    setView(next);
    localStorage.setItem(VIEW_STORAGE_KEY, next);
  };

  const handlePageChange = useCallback((page) => {
    const validPage = Math.max(1, Number(page) || 1);
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('page', String(validPage));
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handlePageSizeChange = useCallback((size) => {
    const nextSize = PAGE_SIZE_OPTIONS.includes(Number(size))
      ? Number(size)
      : DEFAULT_PAGE_SIZE;
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('page', '1');
      if (nextSize === DEFAULT_PAGE_SIZE) {
        nextParams.delete('page_size');
      } else {
        nextParams.set('page_size', String(nextSize));
      }
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handlePageChange(1);
  };

  useEffect(() => {
    if (!searchParams.get('page')) {
      handlePageChange(1);
    }
  }, [handlePageChange, searchParams]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      handlePageChange(totalPages);
    }
  }, [currentPage, handlePageChange, totalPages]);

  useEffect(() => {
    if (!isCreateModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isCreating) setIsCreateModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isCreateModalOpen, isCreating]);

  const handleWorkspaceSelect = (workspace) => {
    if (!workspace || !workspace.id || !workspace.workspace_name) {
      toast.error('Invalid workspace. Please try again.');
      return;
    }

    setSelectedWorkspace(workspace);
    setIsProjectModalOpen(true);
  };

  const openProjectDashboard = (workspace, project) => {
    selectWorkspace(workspace);
    selectProject(String(project.id), project.name);
    updateWorkspaceFn({
      last_active_workspace_id: workspace.id,
      last_active_project_id: project.id,
    });
    navigate(buildWorkspaceDashboardPath(workspace.id, project.id));
  };

  const handleProjectSelect = (project) => {
    if (!selectedWorkspace?.id || !project?.id) {
      toast.error('Invalid project selection. Please try again.');
      return;
    }

    try {
      openProjectDashboard(selectedWorkspace, project);
      setIsProjectModalOpen(false);
      setSelectedWorkspace(null);
      toast.success(`Opened "${project.name}" in ${selectedWorkspace.workspace_name}`);
    } catch (error) {
      console.error('Error selecting project:', error);
      toast.error('Failed to open project dashboard');
    }
  };

  const handleResume = () => {
    if (!lastWorkspace?.id) return;
    if (lastProject?.project_id) {
      openProjectDashboard(lastWorkspace, {
        id: lastProject.project_id,
        name: lastProject.project_name || '',
      });
      return;
    }
    handleWorkspaceSelect(lastWorkspace);
  };

  const handleProjectModalClose = () => {
    setIsProjectModalOpen(false);
    setSelectedWorkspace(null);
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setIsCreateModalOpen(false);
    setWorkspaceName('');
  };

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();

    if (!workspaceName.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    try {
      setIsCreating(true);
      await addWorkspace(workspaceName.trim());

      toast.success('Workspace created successfully');
      setWorkspaceName('');
      setIsCreateModalOpen(false);

      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      refetch();
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast.error(error?.response?.data?.message || 'Failed to create workspace');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!workspaceToDelete || isDeleting) return;

    try {
      setIsDeleting(true);
      await deleteWorkspace(workspaceToDelete.id);

      toast.success('Workspace deleted successfully');
      setWorkspaceToDelete(null);

      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      refetch();
    } catch (error) {
      console.error('Error deleting workspace:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete workspace');
    } finally {
      setIsDeleting(false);
    }
  };

  const menuItems = (workspace) => {
    const owner = isOwner(workspace);
    return [
      {
        id: 'open',
        label: 'Open workspace',
        description: 'Choose a project to work in',
        icon: <LuExternalLink size={15} />,
        onClick: () => handleWorkspaceSelect(workspace),
      },
      {
        id: 'delete',
        label: 'Delete workspace',
        description: owner ? 'Permanently remove this workspace' : 'Only the owner can delete this workspace',
        icon: <LuTrash2 size={15} />,
        danger: true,
        disabled: !owner,
        dividerBefore: true,
        onClick: () => setWorkspaceToDelete(workspace),
      },
    ];
  };

  const listColumns = [
    {
      key: 'workspace_name',
      header: 'Workspace',
      render: (ws) => (
        <div className="rt-ws-name">
          <WorkspaceTile name={ws.workspace_name} size="sm" />
          <span className="rt-ws-name__text" title={ws.workspace_name}>{ws.workspace_name}</span>
          {isActive(ws) && <span className="rt-ws-current">Current</span>}
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Your role',
      width: 140,
      render: (ws) => <RoleChip isOwner={isOwner(ws)} />,
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 200,
      render: (ws) => <span className="rt-ws-muted">{formatCreated(ws.created_at) || '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 150,
      render: (ws) => (
        <div className="rt-ws-row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={() => handleWorkspaceSelect(ws)}
          >
            Open
          </button>
          <ActionMenu items={menuItems(ws)} label={`Actions for ${ws.workspace_name}`} />
        </div>
      ),
    },
  ];

  const isSearching = Boolean(debouncedSearchTerm.trim());
  const showResume = Boolean(lastWorkspace?.id) && !isSearching && currentPage === 1;
  const countLabel = isLoading
    ? 'Loading workspaces…'
    : `${totalCount} ${totalCount === 1 ? 'workspace' : 'workspaces'}${isSearching ? ` matching "${debouncedSearchTerm.trim()}"` : ''}`;

  const renderGrid = () => (
    <div className="rt-ws-grid">
      {workspaces.map((ws) => {
        const created = formatCreated(ws.created_at);
        return (
          <article
            key={ws.id}
            className={`rt-ws-card${isActive(ws) ? ' rt-ws-card--current' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => handleWorkspaceSelect(ws)}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleWorkspaceSelect(ws);
              }
            }}
            aria-label={`Open ${ws.workspace_name}`}
          >
            <div className="rt-ws-card__top">
              <WorkspaceTile name={ws.workspace_name} />
              <div className="rt-ws-card__menu" onClick={(e) => e.stopPropagation()}>
                <ActionMenu items={menuItems(ws)} label={`Actions for ${ws.workspace_name}`} />
              </div>
            </div>
            <h3 className="rt-ws-card__title" title={ws.workspace_name}>{ws.workspace_name}</h3>
            <div className="rt-ws-card__meta">
              <RoleChip isOwner={isOwner(ws)} />
              {isActive(ws) && <span className="rt-ws-current">Current</span>}
            </div>
            <div className="rt-ws-card__footer">
              <span className="rt-ws-muted">
                {created && <LuCalendar size={13} aria-hidden="true" />}
                {created || 'Creation date unavailable'}
              </span>
              <span className="rt-ws-card__open">
                Open <LuArrowRight size={14} aria-hidden="true" />
              </span>
            </div>
          </article>
        );
      })}
      {!isSearching && currentPage === totalPages && (
        <button type="button" className="rt-ws-card rt-ws-card--new" onClick={() => setIsCreateModalOpen(true)}>
          <span className="rt-ws-card--new__icon" aria-hidden="true"><LuPlus size={20} /></span>
          <span className="rt-ws-card__title">New workspace</span>
          <span className="rt-ws-muted">Group projects for a team or product</span>
        </button>
      )}
    </div>
  );

  const renderSkeleton = () => (
    <div className="rt-ws-grid" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div key={idx} className="rt-ws-card rt-ws-card--skeleton">
          <div className="rt-skeleton" style={{ width: 40, height: 40, borderRadius: 10 }} />
          <div className="rt-skeleton" style={{ width: '70%', height: 16 }} />
          <div className="rt-skeleton" style={{ width: '40%', height: 12 }} />
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (error) {
      return (
        <ErrorState
          title="Couldn't load workspaces"
          description={error?.response?.data?.message || 'Please check your connection and try again.'}
          onRetry={() => refetch()}
        />
      );
    }
    if (isLoading) {
      return view === 'list'
        ? <DataTable columns={listColumns} rows={[]} loading skeletonRows={6} />
        : renderSkeleton();
    }
    if (workspaces.length === 0) {
      return isSearching ? (
        <EmptyState
          title="No workspaces found"
          description={`Nothing matches "${debouncedSearchTerm.trim()}". Try a different name.`}
          action={
            <button type="button" className="rt-btn rt-btn--secondary" onClick={() => handleSearchChange('')}>
              Clear search
            </button>
          }
        />
      ) : (
        <EmptyState
          title="No workspaces yet"
          description="Workspaces group your projects, members and settings. Create one to get started."
          action={
            <button type="button" className="rt-btn rt-btn--primary" onClick={() => setIsCreateModalOpen(true)}>
              <LuPlus size={16} aria-hidden="true" /> Create Workspace
            </button>
          }
        />
      );
    }
    return view === 'list' ? (
      <DataTable
        columns={listColumns}
        rows={workspaces}
        rowKey="id"
        onRowClick={handleWorkspaceSelect}
      />
    ) : (
      renderGrid()
    );
  };

  return (
    <PageScaffold
      title="Workspaces"
      description="Switch between testing workspaces or create a new one."
      breadcrumbs={<Breadcrumbs items={[{ label: 'System' }, { label: 'All Workspaces' }]} />}
      actions={
        <button type="button" className="rt-btn rt-btn--primary" onClick={() => setIsCreateModalOpen(true)}>
          <LuPlus size={16} aria-hidden="true" /> Create Workspace
        </button>
      }
    >
      <div className="rt-ws-page">
        {showResume && (
          <section className="rt-ws-resume" aria-label="Continue where you left off">
            <WorkspaceTile name={lastWorkspace.workspace_name} />
            <div className="rt-ws-resume__text">
              <span className="rt-ws-resume__eyebrow">Continue where you left off</span>
              <strong className="rt-ws-resume__title">
                {lastWorkspace.workspace_name}
                {lastProject?.project_name && (
                  <span className="rt-ws-resume__project"> · {lastProject.project_name}</span>
                )}
              </strong>
            </div>
            <button type="button" className="rt-btn rt-btn--primary rt-btn--sm" onClick={handleResume}>
              {lastProject?.project_id ? 'Resume' : 'Choose project'} <LuArrowRight size={14} aria-hidden="true" />
            </button>
          </section>
        )}

        <div className="rt-ws-toolbar">
          <SearchInput
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search workspaces…"
            className="rt-ws-toolbar__search"
          />
          <span className="rt-ws-toolbar__count" aria-live="polite">{countLabel}</span>
          <div className="rt-ws-view" role="group" aria-label="View">
            <button
              type="button"
              className={`rt-ws-view__btn${view === 'grid' ? ' is-active' : ''}`}
              onClick={() => changeView('grid')}
              aria-pressed={view === 'grid'}
              title="Grid view"
            >
              <LuLayoutGrid size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`rt-ws-view__btn${view === 'list' ? ' is-active' : ''}`}
              onClick={() => changeView('list')}
              aria-pressed={view === 'list'}
              title="List view"
            >
              <LuList size={15} aria-hidden="true" />
            </button>
          </div>
        </div>

        {renderBody()}

        {!error && totalCount > 0 && (
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            loading={isLoading}
          />
        )}
      </div>

      {isCreateModalOpen && (
        <div data-rt-modal-overlay className="rt-dialog-overlay" onClick={closeCreateModal} role="presentation">
          <form
            className="rt-dialog rt-ws-create"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateWorkspace}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rt-ws-create-title"
          >
            <div className="rt-ws-create__head">
              <div>
                <h3 className="rt-dialog__title" id="rt-ws-create-title">Create workspace</h3>
                <p className="rt-ws-muted">A workspace groups projects, members and settings for a team or product.</p>
              </div>
              <button
                type="button"
                className="rt-btn rt-btn--tertiary rt-btn--icon"
                onClick={closeCreateModal}
                disabled={isCreating}
                aria-label="Close"
              >
                <LuX size={18} />
              </button>
            </div>

            <label className="rt-ws-field" htmlFor="workspace-name">
              <span className="rt-ws-field__label">
                Workspace name <span className="rt-ws-field__required">*</span>
              </span>
              <input
                type="text"
                id="workspace-name"
                className="rt-ws-field__input"
                placeholder="e.g. Payments QA"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value.slice(0, NAME_MAX_LENGTH))}
                disabled={isCreating}
                maxLength={NAME_MAX_LENGTH}
                autoFocus
                required
              />
              <span className="rt-ws-field__hint">
                {workspaceName.length}/{NAME_MAX_LENGTH}
              </span>
            </label>

            {workspaceName.trim() && (
              <div className="rt-ws-create__preview" aria-hidden="true">
                <WorkspaceTile name={workspaceName.trim()} size="sm" />
                <span>{workspaceName.trim()}</span>
              </div>
            )}

            <div className="rt-dialog__actions">
              <button type="button" className="rt-btn rt-btn--secondary" onClick={closeCreateModal} disabled={isCreating}>
                Cancel
              </button>
              <button type="submit" className="rt-btn rt-btn--primary" disabled={isCreating || !workspaceName.trim()}>
                {isCreating ? 'Creating…' : 'Create workspace'}
              </button>
            </div>
          </form>
        </div>
      )}

      <WorkspaceProjectSelectModal
        isOpen={isProjectModalOpen}
        workspace={selectedWorkspace}
        onClose={handleProjectModalClose}
        onProjectSelect={handleProjectSelect}
      />

      <ConfirmDialog
        open={Boolean(workspaceToDelete)}
        title="Delete workspace?"
        body={`"${workspaceToDelete?.workspace_name}" and its settings will be permanently removed. This action cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting…' : 'Delete workspace'}
        danger
        onCancel={() => !isDeleting && setWorkspaceToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </PageScaffold>
  );
};

export default AllWorkspaces;
